/**
 * Transactions Articles — Russian
 */

import type { HelpArticle } from "../../../types";

export const TRANSACTIONS_ARTICLES_RU: HelpArticle[] = [
  {
    id: "tx-001",
    slug: "transaction-statuses-explained",
    title: "Статусы транзакций",
    description: "Что означает каждый статус",
    category: "transactions",
    popular: true,
    order: 1,
    keywords: ["статус", "ожидание", "завершено", "обработка", "ошибка"],
    content: `
# Статусы транзакций

Каждая транзакция проходит несколько этапов. Вот что означает каждый статус.

## Статусы депозитов

### Обнаружение
- Транзакция видна в блокчейне
- Ожидание включения в блок
- Обычно очень короткий этап

### Средства получены
- Транзакция обнаружена и подтверждена в блокчейне
- Средства у нас
- Ожидание нужного количества подтверждений

### Подтверждение / Ожидание подтверждений
- Транзакция в блоке
- Ожидание подтверждений (зависит от сети)
- Прогресс: X/Y подтверждений
- Ethereum: 12 подтверждений (~2–5 мин)
- Polygon: 128 подтверждений (~3–8 мин)
- Solana: 32 подтверждения (~15–30 сек)

### Конвертация
- Подтверждения получены
- Конвертация крипты в фиат по рыночному курсу
- Автоматический процесс (действий не требуется)

### Платёж отправлен
- Конвертация завершена
- Инструкция на выплату отправлена процессору
- На пути в банк

### Платёж обработан / Зачислено
- Платёж обработан банковской сетью
- Средства скоро появятся на счёте
- Транзакция завершена

### Завершено
- Все этапы пройдены
- Фиат получен на банковский счёт
- Транзакция финализирована

### Ошибка
- Что-то пошло не так на одном из этапов
- Проверьте детали ошибки в транзакции
- При необходимости обратитесь в поддержку

## Статусы выплат

### Ожидание
- В очереди на обработку
- Проверка комплаенса
- Обычно очень коротко

### Обработка
- Подготовка к отправке
- Комплаенс одобрен
- Подготовка банковской инструкции

### Отправлено
- Передано платёжному процессору
- На пути в банковскую сеть
- Отменить нельзя

### В пути
- В банковской сети
- Обрабатывается банками
- Почти готово

### Завершено
- Доставлено в банк
- Должно появиться на счёте
- Может потребоваться время для отображения

### Возвращено
- Банк отклонил перевод
- Средства вернулись на баланс
- Проверьте причину ошибки

## Хронология статусов

### Типичный депозит
\`\`\`
Обнаружение → Средства получены → Ожидание подтверждений → Конвертация →
Платёж отправлен → Платёж обработан → Зачислено
     ↓              ↓                    ↓                  ↓
   ~1 мин        ~1 мин              ~3–10 мин          ~1–2 мин
                                                             ↓
                                                        ~1–2 мин
                                                             ↓
                                                         Готово!
\`\`\`

**Примечание:** Интерфейс может показывать упрощённые статусы. Нажмите «Подробнее» для полной хронологии.

### Типичная выплата (ACH)
\`\`\`
Ожидание → Обработка → Отправлено → Завершено
    ↓          ↓           ↓           ↓
  ~1 мин    ~1–4 часа   ~2–3 дня    Готово!
\`\`\`

## Зависшие транзакции

Если статус не меняется:
- Депозиты: проверьте эксплорер блокчейна
- Выплаты: обратитесь в поддержку через 24 часа
    `.trim(),
  },
  {
    id: "tx-002",
    slug: "finding-a-transaction",
    title: "Поиск транзакции",
    description: "Как найти конкретную транзакцию",
    category: "transactions",
    order: 2,
    keywords: ["найти", "поиск", "транзакция", "история"],
    content: `
# Поиск транзакции

Легко находите любую транзакцию в истории.

## Использование поиска

### Поиск по ID транзакции
1. Откройте «Транзакции»
2. Нажмите на иконку поиска
3. Введите ID транзакции
4. Результаты мгновенно

### Поиск по датам
1. Откройте «Транзакции»
2. Выберите фильтр по дате
3. Укажите начало и конец периода
4. Смотрите отфильтрованные результаты

## Параметры фильтрации

### По типу
- Все транзакции
- Только депозиты
- Только выплаты
- Конвертации

### По статусу
- Все статусы
- Завершённые
- Ожидающие
- С ошибкой

### По сумме
- Любая сумма
- Диапазон
- Больше порога
- Меньше порога

### По активу
- Все активы
- Конкретная криптовалюта
- Конкретная фиатная валюта

## Детали транзакции

Нажмите на транзакцию, чтобы увидеть:
- Полный ID транзакции
- Временные метки
- Детали суммы
- История статусов
- Данные блокчейна (депозиты)
- Банковские данные (выплаты)

## Экспорт

### Скачать историю
1. Откройте «Транзакции»
2. Примените нужные фильтры
3. Нажмите «Экспорт»
4. Выберите формат (CSV, PDF)
5. Скачайте файл

### Для налоговой отчётности
- Доступен экспорт за весь год
- Включает все детали транзакций
- Совместим с бухгалтерским ПО

## Советы

Используйте ID транзакции для быстрого поиска
Задайте диапазон дат для сужения результатов
Сохраняйте часто используемые фильтры
Регулярно экспортируйте для учёта
    `.trim(),
  },
  {
    id: "tx-003",
    slug: "transaction-history",
    title: "История транзакций",
    description: "Просмотр и управление историей транзакций",
    category: "transactions",
    order: 3,
    keywords: ["история", "записи", "прошлые", "просмотр", "список"],
    content: `
# История транзакций

Полная запись всех транзакций в Supabank.

## Доступ к истории

### Веб-версия
1. Нажмите «Транзакции» в боковом меню
2. Смотрите полный список
3. Прокрутите для загрузки ещё

### Мобильная версия
1. Нажмите «Транзакции» в нижнем меню
2. Потяните вниз для обновления
3. Нажмите на транзакцию для деталей

## Обзор истории

### Что включено
- Все депозиты
- Все выплаты
- Конвертации
- Применённые комиссии
- Изменения статусов

### Период хранения
- Полная история сохраняется
- Автоматического удаления нет
- Архив с поиском
- Экспорт в любое время

## Карточки транзакций

Каждая транзакция показывает:
- Иконку типа (депозит/выплата)
- Сумму и валюту
- Дату и время
- Текущий статус
- Краткую информацию

## Детальный просмотр

Нажмите на транзакцию для:
- Полной разбивки
- Всех временных меток
- Номеров ссылок
- Связанных транзакций
- Ссылок на блокчейн
- Банковских ссылок
- Разбивки комиссий

## Организация истории

### Сортировка
- Сначала новые (по умолчанию)
- Сначала старые
- По сумме (от большей)
- По сумме (от меньшей)

### Группировка
- По дате
- По типу
- По статусу
- По валюте

## Хранение данных

- Записи транзакций: бессрочно
- Данные блокчейна: навсегда
- Банковские ссылки: 7 лет
- Подробные логи: 2 года
    `.trim(),
  },
  {
    id: "tx-004",
    slug: "transaction-fees",
    title: "Комиссии транзакций",
    description: "Какие комиссии применяются к транзакциям",
    category: "transactions",
    order: 4,
    keywords: ["комиссии", "стоимость", "цена", "тарифы"],
    content: `
# Комиссии транзакций

Понятная разбивка всех комиссий по транзакциям.

## Типы комиссий

### Комиссии за конвертацию
- Применяются при конвертации крипты в фиат
- Комиссия платформы: 2.0% от суммы
- Комиссия Bridge: ~0.1% от суммы
- Итого: ~2.1% от суммы
- Все комиссии показаны до подтверждения
- Без скрытых наценок

### Сетевые комиссии (депозиты)
- Платит отправитель (вы)
- Покрывает затраты блокчейна
- Зависит от загруженности сети
- Supabank не взимает

### Комиссии за выплату
| Способ | Комиссия |
|--------|----------|
| ACH | $0 |
| Wire (США) | $15 |
| Wire (международный) | $25 |
| SEPA | €0 |
| SPEI | $0 |

## Примеры комиссий

### Пример 1: Депозит $1 000 USDC
- Депозит: $1 000,00
- Комиссия платформы (2%): -$20,00
- Комиссия Bridge (~0.1%): -$1,00
- Выплата (ACH): $0
- **Итого получите: ~$979,00**

### Пример 2: Депозит 1 ETH ($3 000)
- Депозит: $3 000,00
- Комиссия платформы (2%): -$60,00
- Комиссия Bridge (~0.1%): -$3,00
- Выплата (Wire): -$15,00
- **Итого получите: ~$2 922,00**

## Просмотр комиссий

### В транзакции
1. Откройте детали транзакции
2. Прокрутите до раздела «Комиссии»
3. Смотрите детальную разбивку

### В сводке
- Месячная сводка комиссий в настройках
- Общая сумма за месяц
- Сравнение с прошлым месяцем

## Прозрачность комиссий

Все комиссии:
- Показаны до подтверждения
- Детализированы в транзакции
- Включены в выписки
- Никогда не скрыты

## Скидки за объём

Пользователи с большими объёмами могут получить:
- Сниженные комиссии конвертации
- Отмену комиссий за выплату
- Индивидуальные условия
- Обратитесь в отдел продаж
    `.trim(),
  },
  {
    id: "tx-005",
    slug: "refunds-policy",
    title: "Политика возвратов",
    description: "Как работают возвраты в Supabank",
    category: "transactions",
    order: 5,
    keywords: ["возврат", "отмена", "обратно", "политика"],
    content: `
# Политика возвратов

Когда и как обрабатываются возвраты.

## Криптодепозиты

### Можно ли вернуть депозит?
**Как правило, нет.** Криптотранзакции:
- Необратимы по своей природе
- Автоматически конвертируются
- Нельзя «отправить обратно»

### Исключения
Возврат возможен, если:
- Депозит ещё не конвертирован
- Произошла серьёзная системная ошибка
- Рассматривается индивидуально
- Возможна комиссия

## Фиатные выплаты

### Можно ли вернуть выплату?
Если выплата возвращена банком:
- Средства возвращаются на баланс
- Без дополнительной комиссии
- Можно повторить с правильными данными

### Возвращённые выплаты
Частые причины:
- Неверные реквизиты
- Счёт закрыт
- Отказ банка
- Несовпадение имени

## Обращение за помощью

### По депозитам
Обратитесь в поддержку с:
- Хешем транзакции
- Отправленной суммой
- Описанием проблемы
- Причиной запроса возврата

### По выплатам
Обычно автоматически:
- Неудачные выплаты возвращаются сами
- Проверьте статус транзакции
- Обратитесь в поддержку, если не вернулось за 5 дней

## Сроки обработки

| Тип | Срок |
|-----|------|
| Возврат выплаты | 1–3 рабочих дня |
| Рассмотрение депозита | 3–5 рабочих дней |
| Особые случаи | До 14 дней |

## Важно

⚠️ Крипта, отправленная на неправильный адрес, не восстанавливается
⚠️ Конвертации нельзя отменить
⚠️ Разница курсов не возмещается
⚠️ Сетевые комиссии не возвращаются
    `.trim(),
  },
  {
    id: "tx-006",
    slug: "dispute-resolution",
    title: "Разрешение споров",
    description: "Как решить спор по транзакции",
    category: "transactions",
    order: 6,
    keywords: ["спор", "проблема", "жалоба", "решение"],
    content: `
# Разрешение споров

Как решать проблемы и споры по транзакциям.

## Типы споров

### Пропавший депозит
- Отправлено, но не получено
- Зачислена неверная сумма
- Задержка сверх ожидаемой

### Проблемы с выплатой
- Не получено в банке
- Неверная сумма
- Отправлено на неправильный счёт

### Споры по конвертации
- Курс отличается от ожидаемого
- Несогласие с комиссией
- Ошибка расчёта

## Процесс спора

### Шаг 1: Соберите информацию
- ID транзакции
- Хеш транзакции в блокчейне
- Скриншоты проблемы
- Банковские выписки (при необходимости)

### Шаг 2: Обратитесь в поддержку
Напишите на support@supabank.xyz:
- Тема: «Спор — [ID транзакции]»
- Чёткое описание
- Вся документация

### Шаг 3: Расследование
- Рассмотрим в течение 24 часов
- Можем запросить дополнительную информацию
- Проверим все системы и логи

### Шаг 4: Решение
- Сообщим о результатах
- При необходимости примем меры
- Предоставим объяснение

## Сроки

| Этап | Длительность |
|------|--------------|
| Первичный обзор | 24 часа |
| Расследование | 3–5 рабочих дней |
| Решение | До 14 дней |
| Апелляция | Ещё 14 дней |

## Что мы проверяем

- Записи блокчейна
- Внутренние логи транзакций
- Переписку с банком
- Системные метки времени
- Снимки курсов

## Апелляции

Если не согласны с решением:
1. Ответьте на email по спору
2. Предоставьте новую информацию
3. Передадим на проверку старшему специалисту
4. Окончательное решение

## Профилактика

Проверяйте все данные перед отправкой
Используйте правильные сети
Сохраняйте записи транзакций
Сообщайте о проблемах быстро
    `.trim(),
  },
];
